import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { Tag, Save, X } from "lucide-react";
import { createDiscountCode, updateDiscountCode, fetchDiscountCode } from "../actions/discountCodes";
import { fetchCustomersName } from "../actions/customers";
import LoadingProgress from "../components/LoadingProgress";
import { renderInputField, renderSelectField, renderCheckboxField } from "../utils/forms";

class DiscountCodeForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      discountCodeId: null,
    };
  }

  componentDidMount() {
    const { editingDiscountCode, match, fetchDiscountCode } = this.props;
    // Note: fetchCustomersName is called by the parent component (DiscountCodesView)
    // so we don't need to call it here

    // If editingDiscountCode is passed as prop, use it directly (no need to fetch)
    if (editingDiscountCode) {
      this.setState({ isEditing: true, discountCodeId: editingDiscountCode._id });
      // Don't fetch again since we already have the data
    } else if (match && match.params && match.params.id) {
      // Only fetch if using route params (standalone page)
      this.setState({ isEditing: true, discountCodeId: match.params.id });
      fetchDiscountCode(match.params.id);
    }
  }

  componentDidUpdate(prevProps) {
    const { discountCode, discountCodesNeedRefresh } = this.props;

    if (discountCodesNeedRefresh && !prevProps.discountCodesNeedRefresh) {
      if (this.state.discountCodeId) {
        this.props.fetchDiscountCode(this.state.discountCodeId);
      }
    }
  }

  submit = (values) => {
    const { createDiscountCode, updateDiscountCode, history, onSubmit } = this.props;
    const { isEditing, discountCodeId } = this.state;

    return new Promise((resolve, reject) => {
      const action = isEditing
        ? updateDiscountCode(discountCodeId, values)
        : createDiscountCode(values);

      action.then(() => {
        if (onSubmit) {
          onSubmit();
        } else {
          history.push('/discount-codes');
        }
        resolve();
      }).catch((error) => {
        reject(new SubmissionError({ _error: error.message || 'Error saving discount code' }));
      });
    });
  };

  render() {
    const { loading, error, role, discountCode, customers, currentUserId } = this.props;
    const { isEditing } = this.state;
    const isCustomerRole = role !== 'ADMIN';

    if (loading && isEditing) {
      return <LoadingProgress />;
    }

    if (error && isEditing) {
      return (
        <div className="container mt-4">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {error}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => this.props.fetchDiscountCode(this.state.discountCodeId)}
          >
            <i className="fas fa-sync mr-2"></i>
            Reintentar
          </button>
        </div>
      );
    }

    const initialValues = isEditing && discountCode ? {
      code: discountCode.code || '',
      discountType: discountCode.discountType || 'PERCENTAGE',
      discountValue: discountCode.discountValue || '',
      maxUses: discountCode.maxUses || '',
      expiresAt: discountCode.expiresAt ? new Date(discountCode.expiresAt).toISOString().split('T')[0] : '',
      isActive: discountCode.isActive !== undefined ? discountCode.isActive : true,
      customer_id: discountCode.customer_id?._id || discountCode.customer_id || '',
    } : {
      code: '',
      discountType: 'PERCENTAGE',
      discountValue: '',
      maxUses: '',
      expiresAt: '',
      isActive: true,
      customer_id: '',
    };

    return (
      <div className=" mt-4">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">
                  <Tag size={24} className="mr-2" />
                  {isEditing ? 'Editar Código de Descuento' : 'Nuevo Código de Descuento'}
                </h4>
              </div>
              <div className="card-body">
                <form onSubmit={this.props.handleSubmit(this.submit)}>
                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="code"
                        component={renderInputField}
                        label="Código *"
                        placeholder="DESCUENTO2024"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>

                    <div className="col-md-6">
                      {isCustomerRole ? (
                        <div className="form-group">
                          <label>Cliente</label>
                          <p className="form-control-plaintext font-weight-bold">
                            {this.props.auth.fullname}
                          </p>
                          <Field name="customer_id" component="input" type="hidden" />
                        </div>
                      ) : (
                        <Field
                          name="customer_id"
                          component={renderSelectField}
                          label="Cliente *"
                        >
                          <option value="">Seleccionar cliente...</option>
                          {customers && customers.filter(customer => customer.Type === "GENERAL" || customer.Type === 'LICENCIA_BASICA' || customer.Type === 'LICENCIA_PRO').sort((a, b) => a.Fullname.localeCompare(b.Fullname)).map(customer => (
                            <option key={customer._id} value={customer._id}>
                              {customer.Fullname}
                            </option>
                          ))}
                        </Field>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="discountType"
                        component={renderSelectField}
                        label="Tipo de Descuento"
                      >
                        <option value="PERCENTAGE">Porcentaje (%)</option>
                        <option value="FIXED">Valor fijo (€)</option>
                      </Field>
                    </div>

                    <div className="col-md-6">
                      <Field
                        name="discountValue"
                        component={renderInputField}
                        type="number"
                        step="0.01"
                        min="0"
                        label={`Valor del Descuento * ${this.props.formValues?.discountType === 'PERCENTAGE' ? '(%)' : '(€)'}`}
                        placeholder={this.props.formValues?.discountType === 'PERCENTAGE' ? '10.00' : '5.00'}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Field
                        name="maxUses"
                        component={renderInputField}
                        type="number"
                        min="1"
                        label="Máximo de Usos"
                        placeholder="Dejar vacío para ilimitado"
                      />
                      <small className="form-text text-muted">
                        Dejar vacío para usos ilimitados
                      </small>
                    </div>

                    <div className="col-md-6">
                      <Field
                        name="expiresAt"
                        component={renderInputField}
                        type="date"
                        label="Fecha de Expiración"
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <small className="form-text text-muted">
                        Dejar vacío para sin expiración
                      </small>
                    </div>
                  </div>

                  <div className="row mt-3 mb-3">
                    <div className="col-md-6">
                      <Field
                        name="isActive"
                        component={renderCheckboxField}
                        label="Código activo"
                      />
                    </div>
                  </div>

                  {this.props.error && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      {this.props.error}
                    </div>
                  )}

                  <div className="form-group mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary mr-2"
                      disabled={this.props.submitting}
                    >
                      <Save size={16} className="mr-2" />
                      {this.props.submitting ? 'Guardando...' : (isEditing ? 'Actualizar Código' : 'Crear Código')}
                    </button>
                    {this.props.onCancel ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={this.props.onCancel}
                      >
                        <X size={16} className="mr-2" />
                        Cancelar
                      </button>
                    ) : (
                      <Link to="/discount-codes" className="btn btn-secondary">
                        <X size={16} className="mr-2" />
                        Cancelar
                      </Link>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const validate = (values) => {
  const errors = {};
  if (!values.code) errors.code = 'El código es requerido';
  if (!values.discountValue) errors.discountValue = 'El valor del descuento es requerido';
  if (!values.customer_id) errors.customer_id = 'El cliente es requerido';

  const discountValue = parseFloat(values.discountValue);
  if (values.discountType === 'PERCENTAGE' && (discountValue < 0 || discountValue > 100)) {
    errors.discountValue = 'El porcentaje debe estar entre 0 y 100';
  } else if (values.discountType === 'FIXED' && discountValue < 0) {
    errors.discountValue = 'El valor fijo debe ser positivo';
  }

  if (values.maxUses && parseInt(values.maxUses) < 1) {
    errors.maxUses = 'El número máximo de usos debe ser mayor que 0';
  }

  return errors;
};

const mapStateToProps = (state, ownProps) => {
  const isEditing = ownProps.editingDiscountCode || (ownProps.match && ownProps.match.params && ownProps.match.params.id);
  // Use editingDiscountCode directly if available, otherwise look it up in state
  const discountCode = ownProps.editingDiscountCode || (isEditing ? state.discountCodes.discountCodes.find(dc => dc._id === (ownProps.match && ownProps.match.params && ownProps.match.params.id)) : state.discountCodes.discountCode);

  const isCustomerRole = state.auth.role !== 'ADMIN';

  const initialValues = isEditing && discountCode ? {
    code: discountCode.code || '',
    discountType: discountCode.discountType || 'PERCENTAGE',
    discountValue: discountCode.discountValue || '',
    maxUses: discountCode.maxUses || '',
    expiresAt: discountCode.expiresAt ? new Date(discountCode.expiresAt).toISOString().split('T')[0] : '',
    isActive: discountCode.isActive !== undefined ? discountCode.isActive : true,
    customer_id: discountCode.customer_id?._id || discountCode.customer_id || '',
  } : {
    code: '',
    discountType: 'PERCENTAGE',
    discountValue: '',
    maxUses: '',
    expiresAt: '',
    isActive: true,
    customer_id: isCustomerRole ? state.auth.id : '',
  };

  return {
    initialValues,
    auth: state.auth,
    role: state.auth.role,
    currentUserId: state.auth.id,
    customers: state.customers.customers,
    discountCode,
    discountCodesNeedRefresh: state.discountCodes.discountCodesNeedRefresh,
    loading: state.discountCodes.loading,
    error: state.discountCodes.error,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createDiscountCode,
      updateDiscountCode,
      fetchDiscountCode,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'discountCodeForm',
    validate,
    enableReinitialize: true
  })(DiscountCodeForm)
);